import React from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { User, LogOut, Sparkles } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import logo from '../../../assets/PV_logo_longform.png';

const Header = ({ showAuthButtons = false, pageTitle = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authStatus, user, plan, signOut } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <header className="bg-surface/50 backdrop-blur-lg z-20 border-b border-primary/20 sticky top-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Left side: Logo and Page Title */}
          <div className="flex items-center gap-4">
            <Link to={authStatus === 'authenticated' ? "/vaults" : "/"}>
              <img src={logo} alt="PodVault Logo" className="h-10" />
            </Link>
            {pageTitle && (
              <>
                <span className="text-2xl font-bold text-slate-600">|</span>
                <h1 className="text-3xl font-bold text-white">{pageTitle}</h1>
              </>
            )}
          </div>

          {/* Right side: Auth or User Menu */}
          <div className="flex items-center gap-4">
            {authStatus === 'loading' ? (
              // While authStatus is 'loading', render a placeholder
              <div className="h-10 w-48 bg-slate-800 animate-pulse rounded-md"></div>
            ) : authStatus === 'authenticated' && user ? (
              // Authenticated users see User Menu
              <>
                <div className="relative group">
                  <button className="flex items-center gap-2 p-2 rounded-full bg-primary/10 border border-primary/30 text-textSecondary hover:text-white transition-colors">
                    <User className="w-5 h-5" />
                  </button>
                  <div className="absolute right-0 mt-2 w-48 origin-top-right bg-surface rounded-lg shadow-lg py-1 invisible opacity-0 scale-95 transform transition-all duration-200 group-hover:visible group-hover:opacity-100 group-hover:scale-100">
                    <button
                      onClick={handleSignOut}
                      className="flex items-center gap-3 px-4 py-2 text-sm text-textSecondary hover:bg-primary/10 hover:text-white w-full text-left transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Log Out</span>
                    </button>
                  </div>
                </div>
                {/* Upgrade Button for Free Plan */}
                {plan === 'free' && (
                  <button
                    className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-fuchsia-500 text-white font-bold rounded-lg shadow hover:shadow-glow-emerald-lg hover:scale-105 transition-all text-sm flex items-center gap-2"
                    onClick={() => navigate('/checkout', { state: { from: location } })}
                  >
                    <Sparkles className="w-5 h-5" />
                    Upgrade to Pro
                  </button>
                )}
              </>
            ) : (
              // Unauthenticated users see auth buttons
              showAuthButtons && (
                <>
                  <button
                    onClick={() => navigate('/auth?mode=login')}
                    className="px-4 py-2 text-textSecondary hover:text-white transition-colors"
                  >
                    Log In
                  </button>
                  <button
                    onClick={() => navigate('/auth?mode=signup')}
                    className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-fuchsia-500 text-white font-bold rounded-lg shadow hover:shadow-glow-emerald-lg hover:scale-105 transition-all text-sm"
                  >
                    Sign Up
                  </button>
                </>
              )
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
